import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal, TemplateRef, viewChild } from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { Sort } from '@angular/material/sort';
import {
  CardComponent,
  ExpandListComponent,
  ExpandListConfiguration,
  ExpandListSort,
  FeedbackFailComponent,
} from 'ok-angular-shared/components';
import { Organization } from 'src/app/features/organization/organization-models';
import {
  ChargingStationLocation,
  ChargingStationLocationDisplay,
  ChargingStationLocationSearchEvent,
} from 'src/app/features/charging-station-location/charging-station-location-model';
import { PagedResultV2 } from 'src/app/shared/models/paging';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { ChargingStationLocationDetailsComponent } from 'src/app/features/charging-station-location/charging-station-location-details/charging-station-location-details.component';
import { ChargingStationLocationActionsService } from '../../../../../features/charging-station-location/charging-station-location-actions.service';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { AuthService } from 'src/app/core/auth/auth.service';
import { ChargingStationLocationFiltersComponent } from '../../../charging-station-location-filters/charging-station-location-filters.component';
import { Department } from 'src/app/features/department/department.model';
import { AccessGroup } from 'src/app/features/access-groups/access-group.model';
import { PricePolicy } from 'src/app/features/prices/price-policies/interfaces/price-policy';
import { FormatAddressPipe } from '../../../../pipes/format-address.pipe';
import { TranslateLocationStatusEnum } from '../../../../pipes/translate-location-status-enum.pipe';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import { Address } from 'src/app/shared/models/address';
import { ChargingStationLocationsStateService } from 'src/app/features/charging-station-location/charging-station-location-overview/charging-station-locations.state.service';

interface LocationFilters {
  query: string | null;
  organizationId: number | null;
  departmentId: number | null;
  accessGroupIdentifier: string | null;
  pricePolicyId: number | null;
  locationsWithErrorsInSetup: boolean;
}

@Component({
  selector: 'adm-tab-locations',
  templateUrl: './tab-locations.component.html',
  styleUrls: ['./tab-locations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ChargingStationLocationsStateService],
  imports: [
    ExpandListComponent,
    CardComponent,
    FeedbackFailComponent,
    ChargingStationLocationDetailsComponent,
    ChargingStationLocationFiltersComponent,
    FormatAddressPipe,
    TranslateLocationStatusEnum,
    MatTooltip,
    DatePipe,
  ],
})
export class TabLocationsComponent {
  private readonly locationService = inject(ChargingStationLocationService);
  private readonly environmentLoaderService = inject(EnvironmentLoaderService);
  private readonly chargingStationLocationActionsService = inject(ChargingStationLocationActionsService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly state = inject(ChargingStationLocationsStateService);
  private readonly locationsPageSize = 25;

  public statusColumnTemplate = viewChild<TemplateRef<unknown>>('statusColumnTemplate');
  public addressColumnTemplate = viewChild<TemplateRef<unknown>>('addressColumnTemplate');
  public limitedAccessColumnTemplate = viewChild<TemplateRef<unknown>>('limitedAccessColumnTemplate');
  public appVisibilityStatusColumnTemplate = viewChild<TemplateRef<unknown>>('appVisibilityStatusColumnTemplate');

  organization = input.required<Organization>();

  isPricePolicyViewer = this.authService.hasRole(this.environmentLoaderService.virtualRoles[Permission.PricePolicyRead]);

  locationsLoading = signal(false);
  resetAllFiltersTrigger = signal<{ reset: boolean }>({ reset: false });
  filters = signal<LocationFilters>({
    query: null,
    organizationId: null,
    departmentId: null,
    accessGroupIdentifier: null,
    pricePolicyId: null,
    locationsWithErrorsInSetup: false,
  });
  locations = signal<PagedResultV2<ChargingStationLocationDisplay>>({
    items: [],
    hasNextPage: 0,
    pageIndex: 0,
    pageSize: this.locationsPageSize,
  });
  currentSort = signal<ExpandListSort<ChargingStationLocationDisplay> | undefined>(undefined);

  locationExpandListConfig = computed<ExpandListConfiguration<ChargingStationLocationDisplay>>(() => ({
    columns: [
      {
        field: 'operatingDate',
        header: '',
        width: '50px',
        disableSort: true,
        sortField: 'operatingDate',
        displayTemplate: this.statusColumnTemplate(),
      },
      { field: 'name', header: 'Navn', sortField: 'Name' },
      { field: 'address', header: 'Adresse', sortField: 'road', displayTemplate: this.addressColumnTemplate() },
      {
        field: 'restricted',
        header: 'Begrænset adgang',
        sortField: 'Restricted',
        width: '200px',
        displayTemplate: this.limitedAccessColumnTemplate(),
      },
      {
        field: 'appVisibilityStatus',
        header: 'App ikon',
        sortField: 'AppVisibilityStatus',
        width: '200px',
        displayTemplate: this.appVisibilityStatusColumnTemplate(),
      },
    ],
    actions: this.chargingStationLocationActionsService.createLocationActions(locationIdentifier => {
      this.state.updateLocationDetails.next(locationIdentifier);
      this.refreshLocations();
    }),
  }));

  constructor() {
    toObservable(this.organization)
      .pipe(
        tap(organization => {
          this.locationsLoading.set(true);
          this.locations.set({ items: [], hasNextPage: 0, pageIndex: 0, pageSize: this.locationsPageSize });
          this.filters.set({
            query: null,
            organizationId: organization.id,
            departmentId: null,
            accessGroupIdentifier: null,
            pricePolicyId: null,
            locationsWithErrorsInSetup: false,
          });
          this.resetAllFiltersTrigger.set({ reset: true });
        }),
        switchMap(() => this.getLocations()),
        tap(response => {
          this.locations.set({
            items: response.items ?? [],
            hasNextPage: response.hasNextPage ?? 0,
            pageIndex: response.pageIndex ?? 0,
            pageSize: response.pageSize ?? this.locationsPageSize,
          });
          this.locationsLoading.set(false);
        }),
        catchError(() => {
          this.locationsLoading.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  searchInput(source: Observable<string>) {
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(searchTerm => {
      this.filters.update(current => ({ ...current, query: searchTerm }));
      this.refreshLocations();
    });
  }

  onSortChange(sort: ExpandListSort<ChargingStationLocationDisplay>): void {
    this.currentSort.set(sort);
    this.refreshLocations();
  }

  onOrganizationSelected(organization: Organization | null): void {
    const organizationId = organization?.id ?? null;
    if (this.filters().organizationId === organizationId) {
      return;
    }

    this.filters.update(current => ({ ...current, organizationId }));
    this.refreshLocations();
  }

  onDepartmentSelected(department: Department | null): void {
    const departmentId = department?.id ?? null;
    if (this.filters().departmentId === departmentId) {
      return;
    }

    this.filters.update(current => ({ ...current, departmentId }));
    this.refreshLocations();
  }

  onAccessGroupSelected(accessGroup: AccessGroup | null): void {
    const accessGroupIdentifier = accessGroup?.identifier ?? null;
    if (this.filters().accessGroupIdentifier === accessGroupIdentifier) {
      return;
    }

    this.filters.update(current => ({ ...current, accessGroupIdentifier }));
    this.refreshLocations();
  }

  onPricePolicySelected(pricePolicy: PricePolicy | null): void {
    const pricePolicyId = pricePolicy?.id ?? null;
    if (this.filters().pricePolicyId === pricePolicyId) {
      return;
    }

    this.filters.update(current => ({ ...current, pricePolicyId }));
    this.refreshLocations();
  }

  updateLocationWithErrorFilter(checked: boolean): void {
    if (this.filters().locationsWithErrorsInSetup === checked) {
      return;
    }

    this.filters.update(current => ({ ...current, locationsWithErrorsInSetup: checked }));
    this.refreshLocations();
  }

  resetAllFilters(): void {
    this.filters.set({
      query: null,
      organizationId: this.organization().id,
      departmentId: null,
      accessGroupIdentifier: null,
      pricePolicyId: null,
      locationsWithErrorsInSetup: false,
    });
    this.resetAllFiltersTrigger.set({ reset: true });
    this.refreshLocations();
  }

  setOperatingDate(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.setOperatingDate(location, () => this.refreshLocations());
  }

  editChargingStationLocationDefault(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.editChargingStationLocationDefault(location, () => this.refreshLocations());
  }

  editChargingStationLocation(location: ChargingStationLocationDisplay) {
    this.chargingStationLocationActionsService.editChargingStationLocation(location, chargingStationLocationIdentifier => {
      if (chargingStationLocationIdentifier) {
        this.refreshLocations();
      }
    });
  }

  editPricePolicyForAllConnectors(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.editPricePolicyForAllConnectors(location, () => this.refreshLocations());
  }

  private getLocations() {
    const currentFilters = this.filters();
    const searchEvent = new ChargingStationLocationSearchEvent({ pageIndex: 0, pageSize: this.locationsPageSize, length: 0 });
    searchEvent.organizationId = currentFilters.organizationId ?? undefined;
    searchEvent.query = currentFilters.query;
    searchEvent.departmentId = currentFilters.departmentId ?? undefined;
    searchEvent.accessGroupIdentifier = currentFilters.accessGroupIdentifier ?? undefined;
    searchEvent.pricePolicyId = currentFilters.pricePolicyId ?? undefined;
    searchEvent.locationsWithErrorsInSetup = currentFilters.locationsWithErrorsInSetup;
    const sort = this.currentSort();
    if (sort) {
      searchEvent.sorting = { active: sort.column as string, direction: sort.direction } satisfies Sort;
    }

    return this.locationService.getChargingStationLocations(searchEvent).pipe(
      catchError(() => of({ items: [], hasNextPage: 0, pageIndex: 0, pageSize: this.locationsPageSize })),
      map(response => ({
        ...response,
        items: response.items.map(location => ({
          ...location,
          address: new Address(
            location.city,
            location.postalCode,
            location.road,
            location.number,
            location.letter,
            location.floor,
            location.door
          ),
        })),
      }))
    );
  }

  private refreshLocations() {
    this.locationsLoading.set(true);

    this.getLocations()
      .pipe(
        tap(response => {
          this.locations.set({
            items: response.items ?? [],
            hasNextPage: response.hasNextPage ?? 0,
            pageIndex: response.pageIndex ?? 0,
            pageSize: response.pageSize ?? this.locationsPageSize,
          });
          this.locationsLoading.set(false);
        }),
        catchError(() => {
          this.locationsLoading.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
